// Settings → Team: who can sign in to the admin and the iPhone app.
// Owner-only (Settings is owner-only; /api/users is owner-only on the server).
// Passwords are never chosen here: a temporary one is generated, shown once, and the person
// changes it after signing in. Any change to role, studios, link, active or password signs them out.
import { useState, useEffect, useCallback } from "react";
import { useAdminApi } from "../adminUtils";
import { useSession } from "../../../auth/session";
import { useStudio } from "../../../studios/StudioContext";

const STUDIO_LABELS = { "iy-sp": "IY-SP", flourish: "Flourish", demo: "Demo" };

function tempPassword() {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789";
  const buf = new Uint32Array(12);
  window.crypto.getRandomValues(buf);
  return Array.from(buf, n => chars[n % chars.length]).join("");
}

function fmtDate(d) {
  if (!d) return "never";
  return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function UserEditor({ initial, teachers, studioOptions, onCancel, onSave }) {
  const isNew = !initial.id;
  const [form, setForm] = useState({
    name: initial.name ?? "",
    email: initial.email ?? "",
    role: initial.role ?? "teacher",
    studios: initial.studios ?? (studioOptions.length === 1 ? studioOptions : []),
    teacherId: initial.teacherId ?? "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const pickTeacher = id => {
    const t = teachers.find(x => x._id === id);
    setForm(f => ({
      ...f,
      teacherId: id,
      name: f.name || t?.name || "",
      email: f.email || t?.email || "",
    }));
  };
  const toggleStudio = s => set("studios", form.studios.includes(s) ? form.studios.filter(x => x !== s) : [...form.studios, s]);

  const submit = async () => {
    if (!form.name.trim() || !form.email.includes("@")) { setError("Name and a valid email are required."); return; }
    setSaving(true); setError("");
    try {
      await onSave({
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        role: form.role,
        studios: form.studios,
        teacherId: form.teacherId || null,
      });
    } catch (e) { setError(e.message); setSaving(false); }
  };

  return (
    <div style={{ marginTop: "0.75rem" }}>
      <div className="adm-form-grid" style={{ maxWidth: "480px" }}>
        <label className="adm-form-label">Teacher (their classes)
          <select className="adm-input" value={form.teacherId} onChange={e => pickTeacher(e.target.value)}>
            <option value="">— not linked —</option>
            {teachers.map(t => <option key={t._id} value={t._id}>{t.name}</option>)}
          </select>
        </label>
        <label className="adm-form-label">Name
          <input className="adm-input" value={form.name} onChange={e => set("name", e.target.value)} />
        </label>
        <label className="adm-form-label">Sign-in email
          <input className="adm-input" type="email" value={form.email} onChange={e => set("email", e.target.value)} />
        </label>
        <label className="adm-form-label">Access
          <select className="adm-input" value={form.role} onChange={e => set("role", e.target.value)}>
            <option value="teacher">Teacher — their own classes only</option>
            <option value="owner">Owner — everything</option>
          </select>
        </label>
        {studioOptions.length > 1 && (
          <div className="adm-form-label">Studios
            <div style={{ display: "flex", gap: "1rem", marginTop: "0.25rem" }}>
              {studioOptions.map(s => (
                <label key={s} style={{ display: "flex", alignItems: "center", gap: "0.35rem", fontWeight: 400 }}>
                  <input type="checkbox" checked={form.studios.includes(s)} onChange={() => toggleStudio(s)} />
                  {STUDIO_LABELS[s] ?? s}
                </label>
              ))}
            </div>
          </div>
        )}
      </div>
      {form.role === "teacher" && !form.teacherId && (
        <p className="adm-hint">Not linked to a teacher: they'll sign in but won't see any classes.</p>
      )}
      {isNew && <p className="adm-hint">A temporary password is created and shown once after you add them.</p>}
      {error && <p className="adm-error">{error}</p>}
      <div className="adm-modal-actions" style={{ justifyContent: "flex-start", marginTop: "0.75rem" }}>
        <button className="adm-btn adm-btn--ghost" onClick={onCancel}>Cancel</button>
        <button className="adm-btn adm-btn--primary" onClick={submit} disabled={saving}>
          {saving ? "Saving..." : isNew ? "Add login" : "Save changes"}
        </button>
      </div>
    </div>
  );
}

export default function TeamSection() {
  const { API, AUTH } = useAdminApi();
  const session = useSession(API);
  const meId = session?.user?.id;
  const studio = useStudio();
  const studioId = studio?.studioId;
  const studioOptions = ["iy-sp", "flourish"].includes(studioId) ? ["flourish", "iy-sp"] : [studioId].filter(Boolean);

  const [users, setUsers]       = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState("");
  const [editing, setEditing]   = useState(null);   // null | "new" | user id
  const [creds, setCreds]       = useState(null);   // { name, email, password, reset }
  const [copied, setCopied]     = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const [uRes, tRes] = await Promise.all([
        fetch(`${API}/users`, { headers: AUTH }),
        fetch(`${API}/teachers`, { headers: AUTH }),
      ]);
      if (!uRes.ok) throw new Error((await uRes.json().catch(() => ({}))).error || `HTTP ${uRes.status}`);
      setUsers(await uRes.json());
      setTeachers((await tRes.json().catch(() => ({}))).teachers ?? []);
    } catch (e) { setError(`Couldn't load the team: ${e.message}`); }
    finally { setLoading(false); }
  }, [API, AUTH]);

  useEffect(() => { load(); }, [load]);

  const call = async (method, path, body) => {
    const res = await fetch(`${API}/users${path}`, {
      method, headers: AUTH, body: body ? JSON.stringify(body) : undefined,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
    return data;
  };

  const showCreds = c => { setCopied(false); setCreds(c); };

  const create = async body => {
    const password = tempPassword();
    await call("POST", "", { ...body, password });
    setEditing(null);
    showCreds({ name: body.name, email: body.email, password, reset: false });
    load();
  };

  const update = id => async body => {
    await call("PATCH", `/${id}`, body);
    setEditing(null);
    load();
  };

  const resetPassword = async u => {
    if (!window.confirm(`Reset ${u.name}'s password? They'll be signed out everywhere.`)) return;
    const password = tempPassword();
    try {
      await call("PATCH", `/${u.id}`, { password });
      showCreds({ name: u.name, email: u.email, password, reset: true });
      load();
    } catch (e) { setError(e.message); }
  };

  const setActive = async (u, active) => {
    if (!active && !window.confirm(`Turn off ${u.name}'s login? They'll be signed out and can't sign in until you turn it back on.`)) return;
    try { await call("PATCH", `/${u.id}`, { active }); load(); }
    catch (e) { setError(e.message); }
  };

  const copyCreds = async () => {
    const url = `${window.location.origin}${window.location.pathname}`;
    const text = `Sign in at ${url}\nEmail: ${creds.email}\nTemporary password: ${creds.password}\nPlease change your password after signing in.`;
    try { await navigator.clipboard.writeText(text); setCopied(true); } catch { setCopied(false); }
  };

  const teacherName = id => teachers.find(t => t._id === id)?.name;

  return (
    <div className="adm-settings-section">
      <h3 className="adm-settings-heading">Team</h3>
      <p className="adm-hint">Who can sign in to the admin and the iPhone app.</p>

      {creds && (
        <div className="adm-email-chip" style={{ display: "block", marginBottom: "0.75rem" }}>
          <p style={{ margin: 0 }}><strong>{creds.reset ? "New temporary password" : "Login created"} for {creds.name}</strong></p>
          <p style={{ margin: "0.4rem 0" }}>
            Email: {creds.email}<br />
            Temporary password: <code style={{ fontSize: "1rem", userSelect: "all" }}>{creds.password}</code>
          </p>
          <p className="adm-hint" style={{ margin: "0 0 0.5rem" }}>Shown once. Copy it now and send it to them privately; they can change it after signing in.</p>
          <div style={{ display: "flex", gap: "0.5rem" }}>
            <button className="adm-btn adm-btn--sm" onClick={copyCreds}>{copied ? "✓ Copied" : "Copy sign-in details"}</button>
            <button className="adm-btn-ghost-sm" onClick={() => setCreds(null)}>Done</button>
          </div>
        </div>
      )}

      {loading && <p className="adm-loading">Loading…</p>}
      {error && <p className="adm-error">{error}</p>}

      {!loading && (
        <div className="adm-teacher-list">
          {users.map(u => editing === u.id
            ? (
              <UserEditor key={u.id} initial={u} teachers={teachers} studioOptions={studioOptions}
                onCancel={() => setEditing(null)} onSave={update(u.id)} />
            ) : (
              <div key={u.id} className="adm-teacher-row" style={u.active ? undefined : { opacity: 0.55 }}>
                <span className="adm-teacher-thumb adm-teacher-thumb--empty">{(u.name || "?")[0]}</span>
                <div className="adm-teacher-info">
                  <strong>{u.name}{u.id === meId ? " (you)" : ""}</strong>
                  <span>
                    {u.email} · {u.role === "owner" ? "Owner" : "Teacher"}
                    {studioOptions.length > 1 && u.role === "teacher"
                      ? ` · ${(u.studios || []).map(s => STUDIO_LABELS[s] ?? s).join(", ") || "no studio"}` : ""}
                    {u.role === "teacher"
                      ? (teacherName(u.teacherId) ? ` · classes as ${teacherName(u.teacherId)}` : " · not linked to a teacher")
                      : ""}
                  </span>
                  <span>{u.active ? `Last sign-in: ${fmtDate(u.lastLoginAt)}` : "Login turned off"}</span>
                </div>
                <button className="adm-btn-ghost-sm" onClick={() => setEditing(u.id)}>Edit</button>
                <button className="adm-btn-ghost-sm" onClick={() => resetPassword(u)}>Reset password</button>
                {u.id !== meId && (
                  <button className="adm-btn-ghost-sm" onClick={() => setActive(u, !u.active)}>
                    {u.active ? "Turn off" : "Turn on"}
                  </button>
                )}
              </div>
            ))}
        </div>
      )}

      {editing === "new"
        ? <UserEditor initial={{}} teachers={teachers} studioOptions={studioOptions}
            onCancel={() => setEditing(null)} onSave={create} />
        : (
          <button className="adm-btn adm-btn--sm" style={{ marginTop: "0.75rem" }} onClick={() => setEditing("new")}>
            + Add login
          </button>
        )}
    </div>
  );
}
